/**
 * PDF Preview Modal Component
 * 
 * Displays a PDF document in a modal with options to:
 * - Preview PDF inline
 * - Download PDF
 * - Change template type (quote, invoice, simple)
 * - Select language
 * 
 * MAGE-204
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import api from '../services/api';

// ==========================================
// Styled Components
// ==========================================

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1000px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: #1f2937;
  }
`;

const ControlBar = styled.div`
  padding: 0.75rem 1.5rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Select = styled.select`
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  
  &.primary {
    background: #3b82f6;
    color: white;
    border: none;
    
    &:hover {
      background: #2563eb;
    }
  }
  
  &.secondary {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RefreshButton = styled(Button)`
  margin-left: auto;
`;

const PreviewContainer = styled.div`
  flex: 1;
  padding: 1rem;
  overflow: hidden;
  background: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IframeWrapper = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const PdfIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  gap: 1rem;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  gap: 1rem;
  text-align: center;
  
  h3 {
    margin: 0;
    font-size: 1.125rem;
  }
  
  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterInfo = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

// ==========================================
// Component
// ==========================================

function PdfPreviewModal({ 
  orderId, 
  orderNumber, 
  onClose,
  defaultTemplate = 'quote',
  defaultLanguage = 'en'
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  
  const [template, setTemplate] = useState(defaultTemplate);
  const [language, setLanguage] = useState(defaultLanguage);

  // Fetch PDF as blob and create object URL
  const loadPdf = async () => {
    setLoading(true);
    setError('');
    
    // Revoke previous URL to prevent memory leaks
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    
    try {
      const response = await api.get(`/orders/${orderId}/pdf`, {
        params: {
          template: template,
          lang: language
        },
        responseType: 'blob'
      });
      
      // Create blob URL for preview
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error('Failed to load PDF:', err);
      setError(err.response?.data?.error || 'Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  // Load PDF on mount and when options change
  useEffect(() => {
    loadPdf();
    
    // Cleanup on unmount
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, template, language]);

  // Handle download
  const handleDownload = () => {
    if (!pdfUrl) return;
    
    const link = document.createElement('a');
    link.href = pdfUrl;
    
    const templateName = template === 'quote' ? 'quote' : template === 'invoice' ? 'invoice' : 'order';
    link.download = `${templateName}_${orderNumber || orderId}.pdf`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle print
  const handlePrint = () => {
    if (!pdfUrl) return;
    
    const iframe = document.querySelector('#pdf-preview-frame');
    if (iframe) {
      iframe.contentWindow.print();
    }
  };

  // Handle open in new tab
  const handleOpenInNewTab = () => {
    if (!pdfUrl) return;
    window.open(pdfUrl, '_blank');
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            📄 PDF Preview {orderNumber && `- ${orderNumber}`}
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        
        <ControlBar>
          <ControlGroup>
            <Label>Template:</Label>
            <Select 
              value={template} 
              onChange={e => setTemplate(e.target.value)}
              disabled={loading}
            >
              <option value="quote">Quote</option>
              <option value="invoice">Invoice</option>
              <option value="simple">Simple</option>
            </Select>
          </ControlGroup>
          
          <ControlGroup>
            <Label>Language:</Label>
            <Select 
              value={language} 
              onChange={e => setLanguage(e.target.value)}
              disabled={loading}
            >
              <option value="en">English</option>
              <option value="de">Deutsch</option>
              <option value="sl">Slovenščina</option>
            </Select>
          </ControlGroup>
          
          <RefreshButton 
            className="secondary" 
            onClick={loadPdf}
            disabled={loading}
          >
            🔄 Refresh
          </RefreshButton>
        </ControlBar>
        
        <PreviewContainer>
          {loading ? (
            <LoadingContainer>
              <Spinner />
              <span>Generating PDF...</span>
            </LoadingContainer>
          ) : error ? (
            <ErrorContainer>
              <h3>⚠️ Error</h3>
              <p>{error}</p>
              <Button className="primary" onClick={loadPdf}>
                Try Again
              </Button>
            </ErrorContainer>
          ) : pdfUrl ? (
            <IframeWrapper>
              <PdfIframe 
                id="pdf-preview-frame"
                src={`${pdfUrl}#toolbar=1&navpanes=0`} 
                title="PDF Preview"
              />
            </IframeWrapper>
          ) : null}
        </PreviewContainer>
        
        <ModalFooter>
          <FooterInfo>
            {!loading && !error && (
              <>Template: {template.charAt(0).toUpperCase() + template.slice(1)} | Language: {language.toUpperCase()}</>
            )}
          </FooterInfo>
          
          <FooterActions>
            <Button className="secondary" onClick={handleOpenInNewTab} disabled={!pdfUrl || loading}>
              🔗 Open in Tab
            </Button>
            <Button className="secondary" onClick={handlePrint} disabled={!pdfUrl || loading}>
              🖨️ Print
            </Button>
            <Button className="primary" onClick={handleDownload} disabled={!pdfUrl || loading}>
              ⬇️ Download
            </Button>
          </FooterActions>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
}

export default PdfPreviewModal;
